import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { TextField, Box, Typography, Alert, CircularProgress } from '@mui/material';
import { styled } from '@mui/material/styles';
import { CreateReviewSchema, type CreateReview, parseError } from '@lov/shared';
import { StarRatingInput } from './StarRatingInput';
import { Button, SideDrawer } from '../../../components/ui';
import { useCreateReview } from '../hooks/useReviews';

interface WriteReviewModalProps {
  open: boolean;
  onClose: () => void;
  bookingId: string | null;
  venueName: string;
}

// Styled Components
const FormSection = styled(Box)({
  marginBottom: 24,
  '&:last-of-type': {
    marginBottom: 0,
  },
});

const Label = styled(Typography)(({ theme }) => ({
  fontFamily: '"IBM Plex Sans", sans-serif',
  fontSize: '0.875rem',
  fontWeight: 600,
  color: theme.palette.text.primary,
  marginBottom: 8,
  display: 'block',
}));

/**
 * WriteReviewModal - Modal for submitting venue reviews
 *
 * Allows users to submit ratings and comments for venues they've booked.
 * Requires a bookingId - users can only review venues they've actually visited.
 *
 * TODO: Add authentication check once useAuth hook is implemented
 * TODO: Add image upload support (requires Cloudflare R2 integration)
 * TODO: Show only for completed bookings in user's booking history
 *
 * @example
 * <WriteReviewModal
 *   open={isOpen}
 *   onClose={() => setIsOpen(false)}
 *   bookingId={booking?.id || null}
 *   venueName={venue.name}
 * />
 */
export function WriteReviewModal({ open, onClose, bookingId, venueName }: WriteReviewModalProps) {
  const { mutate: createReview, isPending, error } = useCreateReview();

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<CreateReview>({
    resolver: zodResolver(CreateReviewSchema),
    defaultValues: {
      bookingId: bookingId || '',
      rating: 0,
      comment: '',
    },
  });

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = async (data: CreateReview) => {
    createReview(data, {
      onSuccess: () => {
        handleClose();
      },
    });
  };

  // If no bookingId, show message that user needs a booking to review
  if (!bookingId) {
    return (
      <SideDrawer
        open={open}
        onClose={handleClose}
        title="Booking Required"
        width={500}
        footer={
          <Button variant="primary" onClick={handleClose}>
            Close
          </Button>
        }
      >
        <Alert severity="info">
          You need to complete a booking at this venue before you can write a review. This ensures
          all reviews are from verified customers.
        </Alert>
      </SideDrawer>
    );
  }

  return (
    <SideDrawer
      open={open}
      onClose={handleClose}
      title={`Write a Review - ${venueName}`}
      loading={isPending}
      width={540}
      footer={
        <>
          <Button variant="secondary" onClick={handleClose} disabled={isPending}>
            Cancel
          </Button>
          <Button
            type="submit"
            form="review-form"
            variant="primary"
            disabled={isPending}
            startIcon={isPending ? <CircularProgress size={16} /> : undefined}
          >
            {isPending ? 'Submitting...' : 'Submit Review'}
          </Button>
        </>
      }
    >
      <form id="review-form" onSubmit={handleSubmit(onSubmit)}>
        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {parseError(error).message}
          </Alert>
        )}

        {/* Rating */}
        <FormSection>
          <Label>
            Rating <span style={{ color: '#D32F2F' }}>*</span>
          </Label>
          <Controller
            name="rating"
            control={control}
            render={({ field }) => (
              <StarRatingInput
                value={field.value}
                onChange={field.onChange}
                error={!!errors.rating}
                helperText={errors.rating?.message}
              />
            )}
          />
        </FormSection>

        {/* Comment */}
        <FormSection>
          <Label>Your Review (Optional)</Label>
          <Controller
            name="comment"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                multiline
                rows={4}
                fullWidth
                placeholder="Share your experience with this venue..."
                error={!!errors.comment}
                helperText={errors.comment?.message}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    fontFamily: '"IBM Plex Sans", sans-serif',
                  },
                }}
              />
            )}
          />
        </FormSection>
      </form>
    </SideDrawer>
  );
}
